import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { coerceArray } from '@partners/utils';
import { IFileValidationHandler, TValidatorError } from '../../model';

// Default set to 50KB to filter out corrupted, blank, or low-quality images
/**
 * Validates that file(s) meet a minimum size threshold.
 * Useful for ensuring uploaded document images (e.g. ID, driver's license)
 * are of sufficient quality and not blank or corrupted.
 *
 * @param minFileSize - Minimum file size (default: 50)
 * @param unit - Size unit, 'KB' or 'MB' (default: 'KB')
 * @returns ValidatorFn that returns a `minFileSize` validation error if any file is too small
 */
export function minFileSizeValidator({ minFileSize, unit }: IFileValidationHandler): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const files: File[] | File | null = control.value;
    if (!files) return null;
    const minSizeInBytes = unit === 'MB' ? minFileSize * 1024 * 1024 : minFileSize * 1024;
    // Handle single file or array of files
    const fileList = coerceArray(files);

    const invalidFiles = fileList.filter((f) => f.size < minSizeInBytes);

    if (invalidFiles.length > 0) {
      const fileSize = {
        allowed: minFileSize + unit,
        actual: unit === 'MB' ? (invalidFiles[0].size / 1024 / 1024).toFixed() : (invalidFiles[0].size / 1024).toFixed()
      };
      const vldResponse: TValidatorError = { minFileSize: fileSize };
      return vldResponse;
    }

    return null;
  };
}
